import isEmpty from "lodash/isEmpty";
import { api } from "../axios";
import { Notification } from "../notification";
import {
  FamilySipScheme,
  FamilyStpScheme,
  FamilySwpScheme,
  PaginatedResponse,
  SchemeFolioHolding,
} from "../../types";
import { transformSchemeFolioHoldings } from "./portfolio";
import {
  BulkAssignFamiliesPayload,
  BulkAssignFamiliesResponse,
  CreateFamilyPayload,
  FamiliesFilter,
  FamiliesResponse,
  FamilyAumOverview,
  FamilyContextResponse,
  FamilyDetail,
  FamilyGoalsSummaryOverview,
  FamilyMonthlySipOverview,
  FamilyRosterResponse,
  FamilySchemeWiseHolding,
  FamilySource,
  UpdateFamilyPayload,
} from "../../types/client";

export type FamilyRecentActivity = Notification & { important?: boolean };
export type FamilyRecentActivityResponse =
  PaginatedResponse<FamilyRecentActivity>;

function parseFamiliesFilters(filters: FamiliesFilter = {}) {
  return Object.entries(filters).reduce(
    (acc, [key, value]) => {
      if (typeof value === "undefined") return acc;
      if (Array.isArray(value)) {
        if (isEmpty(value)) return acc;
        acc[key] = value.join(",");
      } else if (value !== "") {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, string>
  );
}

export function getFamilies({
  page = 1,
  pageSize = 20,
  search = "",
  filters = {},
  sort,
}: {
  page?: number;
  pageSize?: number;
  search?: string;
  filters?: FamiliesFilter;
  sort?: Array<{ id: string; desc: boolean }>;
} = {}): Promise<FamiliesResponse> {
  return api.get("families/", {
    params: {
      page,
      page_size: pageSize,
      ...(search ? { q: search } : {}),
      ...parseFamiliesFilters(filters),
      ...(sort?.length
        ? {
            ordering: sort.map((s) => `${s.desc ? "-" : ""}${s.id}`).join(","),
          }
        : {}),
    },
    paramsSerializer: { indexes: null },
  });
}

export function getFamily(id: number): Promise<FamilyDetail> {
  return api.get(`families/${id}/`);
}

export function getFamilyAum(
  id: number,
  source: FamilySource = "OVERALL"
): Promise<FamilyAumOverview> {
  return api.get(`families/${id}/aum/`, {
    params: { source },
  });
}

export function getFamilyMonthlySip(
  id: number
): Promise<FamilyMonthlySipOverview> {
  return api.get(`families/${id}/monthly-sip/`);
}

// One row per scheme, aggregated across every member of the family.
export function getFamilySchemeWiseHoldings(
  id: number,
  source: FamilySource = "OVERALL"
): Promise<FamilySchemeWiseHolding[]> {
  return api.get("holdings/scheme-wise/", {
    params: { family_id: id, source },
  });
}

// Investor-only: AUM/monthly-SIP for the requesting investor's own family,
// resolved from the auth token rather than a family id in the URL.
export function getMyFamilyAum(
  source: FamilySource = "OVERALL"
): Promise<FamilyAumOverview> {
  return api.get("family/aum/", { params: { source } });
}

export function getMyFamilyMonthlySip(): Promise<FamilyMonthlySipOverview> {
  return api.get("family/monthly-sip/");
}

// Investor-only: roster for the requesting investor's own family, resolved
// from the auth token rather than a family id in the URL.
export function getMyFamilyRoster(
  source: FamilySource = "OVERALL"
): Promise<FamilyRosterResponse> {
  return api.get("family/roster/", { params: { source } });
}

// Investor-only: marks the family feature as viewed for the requesting
// investor, resolved from the auth token rather than a family id in the URL.
export function markMyFamilyFeatureViewed(): Promise<void> {
  return api.post("family/feature-viewed/");
}

// Investor-only bootstrap: which family state the caller is in, plus the
// ready-to-render account-switcher payload.
export function getMyFamilyContext(): Promise<FamilyContextResponse> {
  return api.get("family/context/");
}

export function getFamilyRecentActivity(
  id: number,
  { days = 7, page = 1 }: { days?: number; page?: number } = {}
): Promise<FamilyRecentActivityResponse> {
  return api.get(`families/${id}/recent-activity/`, {
    params: { days, page },
  });
}

export function getFamilyGoalsSummary(
  id: number
): Promise<FamilyGoalsSummaryOverview> {
  return api.get(`families/${id}/goals-summary/`);
}

// Investor-only: goals summary for the requesting investor's own family,
// resolved from the auth token rather than a family id in the URL.
export function getMyFamilyGoalsSummary(): Promise<FamilyGoalsSummaryOverview> {
  return api.get("family/goals-summary/");
}

export function createFamily(
  payload: CreateFamilyPayload
): Promise<FamilyDetail> {
  return api.post("families/", payload);
}

export function updateFamily(
  id: number,
  payload: UpdateFamilyPayload
): Promise<FamilyDetail> {
  return api.patch(`families/${id}/`, payload);
}

export function deleteFamily(id: number): Promise<void> {
  return api.delete(`families/${id}/`);
}

export function bulkAssignFamilies(
  payload: BulkAssignFamiliesPayload
): Promise<BulkAssignFamiliesResponse> {
  return api.post("families/bulk-assign/", payload);
}

// Investor-only: scheme-folio-wise holdings aggregated across the requesting
// investor's family. Same shape as `holdings/scheme-folio-wise/`, with scheme
// rows merged on (scheme_isin, internal_flag) and every member's folios nested
// under them.
export async function getMyFamilyHoldings(
  search?: string
): Promise<Array<SchemeFolioHolding>> {
  const response = await api.get("family/holdings/scheme-folio-wise/", {
    params: { search: search || undefined },
  });
  return transformSchemeFolioHoldings(response.scheme_data);
}

export type MyFamilySipsResponse = PaginatedResponse<FamilySipScheme> & {
  active_count: number;
  monthly_amount: string;
};

export type MyFamilyStpsResponse = PaginatedResponse<FamilyStpScheme> & {
  active_count: number;
  monthly_amount: string;
};

export type MyFamilySwpsResponse = PaginatedResponse<FamilySwpScheme> & {
  active_count: number;
  monthly_amount: string;
};

type MyFamilyPlansParams = {
  page: number;
  pageSize: number;
  search?: string;
};

// Investor-only: SIP/STP/SWP plans across the requesting investor's family,
// paginated like their per-account counterparts in `./plan`. Each comes back
// grouped per scheme, with each member's plans nested under `plans`.
export function getMyFamilySips({
  page,
  pageSize,
  search,
}: MyFamilyPlansParams): Promise<MyFamilySipsResponse> {
  return api.get("family/xsips/", {
    params: { page, page_size: pageSize, search },
  });
}

export function getMyFamilyStps({
  page,
  pageSize,
  search,
}: MyFamilyPlansParams): Promise<MyFamilyStpsResponse> {
  return api.get("family/stps/", {
    params: { page, page_size: pageSize, search },
  });
}

export function getMyFamilySwps({
  page,
  pageSize,
  search,
}: MyFamilyPlansParams): Promise<MyFamilySwpsResponse> {
  return api.get("family/swps/", {
    params: { page, page_size: pageSize, search },
  });
}
